import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { GeoJsonHeatmapLayer } from '@wehunt/map'
import { useTranslation } from '../i18n'
import { COLORS } from '../lib/theme'
import { uiLocale } from '../lib/dateFormat'
import { heatFeatureCollection, heatmapPaint } from '../lib/mapGeoJson'
import type { Team } from '../lib/types'
import { TeamMapView } from './TeamMapView'
import { useTeamMapPresentation } from './teamMapPresentation'
import { WaSelect } from './WaSelect'
import { Icon } from './Icon'

// Heatmap tab for the team detail page: a density visualisation of the team's
// historical reports (harvests and observations) over the hunting ground.
// Long-time users have years of data locked in the legacy system — rendering
// it as heat over the map turns that raw history into hunt-planning insight
// (where game is actually seen and taken), filterable by species, report kind
// and time period.

export interface HeatmapEvent {
  id: string
  /** Species display name — also the grouping key for the species filter. */
  species: string
  kind: 'downed' | 'observed'
  /** ISO date (yyyy-mm-dd). Only year+month matter to the period filter. */
  date: string
  lat: number
  lon: number
}

export interface TeamHeatmapTabProps {
  team: Team
  events: HeatmapEvent[]
}

type KindFilter = 'all' | 'downed' | 'observed'

// ── Period helpers — the slider works in "months since the first data month" ──

function monthIndexOf(date: string, startYear: number): number {
  const y = Number(date.slice(0, 4))
  const m = Number(date.slice(5, 7)) - 1
  return (y - startYear) * 12 + m
}

// ── Heat rendering ────────────────────────────────────────────────────────

// The legacy blob-canvas renderer (grayscale radial stamps → color ramp) is
// gone: MapLibre's native `heatmap` layer type does the same accumulate-and-
// colorize job in the GPU, driven by `HEAT_PAINT` below. `heatmapPaint()` is
// held at module scope — the layer hook diffs paint by object reference, so
// a value rebuilt every render would re-set every paint key every render.
const HEAT_PAINT = heatmapPaint()

// ── Dual-thumb month-range slider ─────────────────────────────────────────

// Two stacked native ranges; the inputs are pointer-transparent except for
// their thumbs, so each thumb stays independently draggable. Styling native
// thumbs needs pseudo-element rules, hence the injected stylesheet.
const RANGE_CSS = `
.wh-heat-range { position:absolute; inset:0; width:100%; height:100%; margin:0; background:none; -webkit-appearance:none; appearance:none; pointer-events:none; }
.wh-heat-range::-webkit-slider-thumb { -webkit-appearance:none; appearance:none; pointer-events:auto; width:18px; height:18px; border-radius:50%; background:#fff; border:2px solid ${COLORS.primary}; box-shadow:0 1px 3px rgba(0,0,0,0.3); cursor:pointer; }
.wh-heat-range::-moz-range-thumb { pointer-events:auto; width:14px; height:14px; border-radius:50%; background:#fff; border:2px solid ${COLORS.primary}; box-shadow:0 1px 3px rgba(0,0,0,0.3); cursor:pointer; }
.wh-heat-range::-moz-range-track { background:none; }
`

function useHeatmapStyles() {
  useEffect(() => {
    if (document.getElementById('wh-heatmap-styles')) return
    const el = document.createElement('style')
    el.id = 'wh-heatmap-styles'
    el.textContent = RANGE_CSS
    document.head.appendChild(el)
  }, [])
}

function MonthRangeSlider({
  max,
  lo,
  hi,
  onChange,
  labelOf,
  ariaLabel,
}: {
  /** Highest month index (inclusive); the low bound is always 0. */
  max: number
  lo: number
  hi: number
  onChange: (lo: number, hi: number) => void
  labelOf: (monthIndex: number) => string
  ariaLabel: string
}) {
  const loPct = (lo / max) * 100
  const hiPct = (hi / max) * 100
  return (
    <div>
      <div style={{ position: 'relative', height: 28 }} role="group" aria-label={ariaLabel}>
        {/* Track + selected segment */}
        <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: 4, borderRadius: 2, background: COLORS.outlineVariant }} />
        <div style={{ position: 'absolute', left: `${loPct}%`, width: `${hiPct - loPct}%`, top: '50%', transform: 'translateY(-50%)', height: 4, borderRadius: 2, background: COLORS.primary }} />
        <input
          type="range"
          className="wh-heat-range"
          min={0}
          max={max}
          step={1}
          value={lo}
          aria-label={`${ariaLabel} — ${labelOf(lo)}`}
          aria-valuetext={labelOf(lo)}
          onChange={(e) => onChange(Math.min(Number(e.target.value), hi), hi)}
        />
        <input
          type="range"
          className="wh-heat-range"
          min={0}
          max={max}
          step={1}
          value={hi}
          aria-label={`${ariaLabel} — ${labelOf(hi)}`}
          aria-valuetext={labelOf(hi)}
          onChange={(e) => onChange(lo, Math.max(Number(e.target.value), lo))}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: COLORS.outline, marginTop: 2 }}>
        <span>{labelOf(0)}</span>
        <span>{labelOf(max)}</span>
      </div>
    </div>
  )
}

// ── Map surface — shared by the inline tab and the full-screen preview ────

// Each surface owns its presentation and view state: the inline map and the
// full-screen one have different sizes (and so different framings), exactly
// like the Map tab's inline/full-screen pair.
function HeatSurface({ team, events, count }: { team: Team; events: HeatmapEvent[]; count: number }) {
  const { t } = useTranslation('teams')
  const presentation = useTeamMapPresentation()
  // Downed reports weigh more than observations: a harvest is the stronger
  // signal about a spot (same weighting the legacy blob renderer used).
  const heatData = useMemo(
    () =>
      heatFeatureCollection(
        events.map((e) => ({ id: e.id, lat: e.lat, lon: e.lon, weight: e.kind === 'downed' ? 0.55 : 0.3 })),
      ),
    [events],
  )
  return (
    <>
      <TeamMapView
        map={team.map}
        interactive={true}
        appearance={presentation.appearance}
        tileSource={presentation.tileSource}
        overlayLayers={presentation.overlayLayers}
        extraControls={presentation.controls}
      >
        <GeoJsonHeatmapLayer id="team-heat" data={heatData} paint={HEAT_PAINT} />
      </TeamMapView>

      {/* Legend + shown-report count, bottom-left over the map. */}
      <div
        style={{
          position: 'absolute',
          left: 12,
          bottom: 12,
          zIndex: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '6px 12px',
          borderRadius: 8,
          background: COLORS.surface,
          boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
          fontSize: 11,
          color: COLORS.onSurfaceVariant,
        }}
      >
        <span style={{ fontWeight: 600, color: COLORS.onSurface }}>
          {t('detail.heatmap.reports', { count })}
        </span>
        <span>{t('detail.heatmap.legendLow')}</span>
        {/* CSS mirror of heatmapPaint()'s 'heatmap-color' ramp stops (mid
            stops omitted — a CSS gradient only needs the two ends and the
            three color waypoints to read the same by eye) — change them
            together. */}
        <span
          aria-hidden
          style={{
            width: 72,
            height: 8,
            borderRadius: 4,
            background: 'linear-gradient(90deg, #ffe873, #ffc93c, #ff9231, #f4511e, #c62828)',
          }}
        />
        <span>{t('detail.heatmap.legendHigh')}</span>
      </div>

      {presentation.panels}
    </>
  )
}

// Full-screen, movable preview of the heatmap — the Map tab's TeamMapFullscreen
// pattern, carrying the tab's current filter selection into the big view.
function HeatmapFullscreen({
  team,
  events,
  count,
  filterBar,
  onClose,
}: {
  team: Team
  events: HeatmapEvent[]
  count: number
  /** The tab's filter controls, mounted again here so the selection can be
      changed without leaving the big view. State lives in the tab, so both
      bars stay in sync. */
  filterBar: ReactNode
  onClose: () => void
}) {
  const { t } = useTranslation('teams')
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    closeRef.current?.focus()
  }, [])
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('detail.map.previewTitle')}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: '#000',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Same filters as the tab, docked above the big map. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          rowGap: 12,
          padding: '10px 16px',
          background: COLORS.surfaceContainerLowest,
        }}
      >
        {filterBar}
      </div>
      <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
        <HeatSurface team={team} events={events} count={count} />
        <button
          ref={closeRef}
          type="button"
          className="wh-btn-icon"
          aria-label={t('detail.map.closeFullscreen')}
          title={t('detail.common.close')}
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 16,
            // Top-LEFT, like the Map tab's preview: the canvas renders its
            // zoom/recenter stack at top-right, so a top-right close would
            // sit on the '+' zoom control.
            left: 16,
            zIndex: 5,
            background: COLORS.surface,
            boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
          }}
        >
          <Icon name="close" size={24} />
        </button>
      </div>
    </div>
  )
}

// ── The tab ───────────────────────────────────────────────────────────────

export function TeamHeatmapTab({ team, events }: TeamHeatmapTabProps) {
  const { t, i18n } = useTranslation('teams')
  useHeatmapStyles()
  const [fullscreen, setFullscreen] = useState(false)

  // The period axis spans the data's whole calendar years, so the slider
  // bounds read as "Jan <first year> … Dec <last year>" like the legacy UI.
  const { startYear, endYear } = useMemo(() => {
    const years = events.map((e) => Number(e.date.slice(0, 4)))
    const now = new Date().getFullYear()
    return {
      startYear: years.length ? Math.min(...years) : now,
      endYear: years.length ? Math.max(...years) : now,
    }
  }, [events])
  const maxMonth = (endYear - startYear + 1) * 12 - 1

  const [species, setSpecies] = useState('all')
  const [kind, setKind] = useState<KindFilter>('all')
  const [range, setRange] = useState<[number, number]>([0, maxMonth])
  useEffect(() => setRange([0, maxMonth]), [maxMonth])

  const speciesOptions = useMemo(() => {
    const names = [...new Set(events.map((e) => e.species))].sort()
    return [
      { value: 'all', label: t('detail.heatmap.allGame') },
      ...names.map((n) => ({ value: n, label: n })),
    ]
  }, [events, t])

  const monthLabel = useCallback(
    (idx: number) => {
      const d = new Date(startYear, idx, 1)
      return d.toLocaleDateString(uiLocale(i18n.language), { month: 'short', year: 'numeric' })
    },
    [startYear, i18n.language],
  )

  // Year quick-pick and slider drive the same range. When the slider sits on
  // something no year option describes, the dropdown shows the custom span.
  const yearOptions = useMemo(() => {
    const opts = [{ value: 'all', label: t('detail.heatmap.allYears') }]
    for (let y = startYear; y <= endYear; y++) opts.push({ value: String(y), label: String(y) })
    const matchesAll = range[0] === 0 && range[1] === maxMonth
    const matchedYear = range[0] % 12 === 0 && range[1] === range[0] + 11 ? startYear + range[0] / 12 : null
    if (!matchesAll && matchedYear == null) {
      opts.push({ value: 'custom', label: `${monthLabel(range[0])} – ${monthLabel(range[1])}` })
    }
    return opts
  }, [t, startYear, endYear, range, maxMonth, monthLabel])

  const yearValue =
    range[0] === 0 && range[1] === maxMonth
      ? 'all'
      : range[0] % 12 === 0 && range[1] === range[0] + 11
        ? String(startYear + range[0] / 12)
        : 'custom'

  const pickYear = (value: string) => {
    if (value === 'custom') return
    if (value === 'all') setRange([0, maxMonth])
    else {
      const lo = (Number(value) - startYear) * 12
      setRange([lo, lo + 11])
    }
  }

  const filtered = useMemo(
    () =>
      events.filter((e) => {
        if (species !== 'all' && e.species !== species) return false
        if (kind !== 'all' && e.kind !== kind) return false
        const m = monthIndexOf(e.date, startYear)
        return m >= range[0] && m <= range[1]
      }),
    [events, species, kind, range, startYear],
  )

  const KINDS: { id: KindFilter; label: string }[] = [
    { id: 'downed', label: t('detail.heatmap.downed') },
    { id: 'observed', label: t('detail.heatmap.observed') },
    { id: 'all', label: t('detail.heatmap.kindAll') },
  ]

  // Built once and mounted in both places that need it: the tab's summary bar
  // and the full-screen preview's top bar. All the state lives here in the
  // parent, so the two instances always show — and drive — the same selection.
  const filterControls = (
    <>
      <WaSelect
        value={species}
        options={speciesOptions}
        onChange={setSpecies}
        ariaLabel={t('detail.heatmap.selectGame')}
        width={180}
      />

      {/* Downed / Observed / All — same segmented-tabs style as the Hunts
          screen's status filter. */}
      <div
        role="group"
        aria-label={t('detail.heatmap.kindAll')}
        style={{ display: 'flex', gap: 2, background: COLORS.surface, padding: 3, borderRadius: 8, flexShrink: 0 }}
      >
        {KINDS.map((k) => {
          const active = kind === k.id
          return (
            <button
              key={k.id}
              type="button"
              onClick={() => setKind(k.id)}
              aria-pressed={active}
              style={{
                border: 'none',
                padding: '6px 12px',
                borderRadius: 6,
                background: active ? COLORS.surfaceContainerLowest : 'transparent',
                boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                fontSize: 12,
                fontWeight: 600,
                color: active ? COLORS.primary : COLORS.outline,
                cursor: 'pointer',
                lineHeight: 1.2,
              }}
            >
              {k.label}
            </button>
          )
        })}
      </div>

      <div style={{ flex: '1 1 260px', minWidth: 220 }}>
        <MonthRangeSlider
          max={maxMonth}
          lo={range[0]}
          hi={range[1]}
          onChange={(lo, hi) => setRange([lo, hi])}
          labelOf={monthLabel}
          ariaLabel={t('detail.heatmap.period')}
        />
      </div>

      <WaSelect
        value={yearValue}
        options={yearOptions}
        onChange={pickYear}
        ariaLabel={t('detail.heatmap.allYears')}
        width={150}
      />
    </>
  )

  return (
    <div className="wh-card" style={{ overflow: 'hidden' }}>
      {/* Filter bar above the map, mirroring the Map tab's summary-bar layout
          so the two map tabs read as siblings. */}
      <div className="wh-map-summary-bar" style={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 12 }}>
        {filterControls}
      </div>

      <div className="wh-map-overview" style={{ position: 'relative' }}>
        <HeatSurface team={team} events={filtered} count={filtered.length} />
        <button
          type="button"
          className="wh-btn-icon"
          aria-label={t('detail.map.openFullscreen')}
          title={t('detail.map.fullscreen')}
          onClick={() => setFullscreen(true)}
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            zIndex: 4,
            background: COLORS.surface,
            boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
          }}
        >
          <Icon name="fullscreen" size={20} />
        </button>
      </div>

      {fullscreen && (
        <HeatmapFullscreen
          team={team}
          events={filtered}
          count={filtered.length}
          filterBar={filterControls}
          onClose={() => setFullscreen(false)}
        />
      )}
    </div>
  )
}
